import React from "react";
import { GoUpload, GoAlert } from "react-icons/go";
import styled from "styled-components";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ $compact }) => ($compact ? '24px 24px 80px' : '80px 24px')};
  text-align: center;
  min-height: calc(100vh - 220px);

  .icon {
    width: 72px;
    height: 72px;
    border-radius: 20px;
    background: ${({ theme }) => (theme.primary || "#3b82f6")}15;
    border: 1px solid ${({ theme }) => (theme.primary || "#3b82f6")}25;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;

    svg {
      font-size: 32px;
      color: ${({ theme }) => theme.primary || "#3b82f6"};
      opacity: 0.85;
    }
  }

  .title {
    font-size: 1.15rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    margin: 0 0 10px;
  }

  .message {
    font-size: 0.85rem;
    color: ${({ theme }) => theme.textSecondary || theme.text};
    opacity: 0.6;
    max-width: 400px;
    line-height: 1.6;
    margin: 0 0 24px;
  }

  .cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border-radius: 8px;
    background: ${({ theme }) => theme.primary || "#3b82f6"};
    color: #fff;
    font-weight: 500;
    font-size: 0.85rem;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }

    svg {
      font-size: 1.1em;
    }
  }

  .hint {
    margin-top: 14px;
    font-size: 0.72rem;
    color: ${({ theme }) => theme.textSecondary || theme.text};
    opacity: 0.4;

    a {
      color: ${({ theme }) => theme.primary || "#3b82f6"};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

export default function EmptyState({
  screen = "products",
  compact = false,
  title: customTitle,
  message: customMessage,
  ctaText: customCtaText,
  onCta: customOnCta,
  icon: customIcon,
  hint: customHint,
}) {
  const goToFiles = () => {
    window.dispatchEvent(new CustomEvent('navigate-dashboard', { detail: 'files' }));
  };

  const goToCertificates = () => {
    window.dispatchEvent(new CustomEvent('navigate-dashboard', { detail: 'certificates' }));
  };

  const messages = {
    products: {
      title: "Nenhum produto disponível",
      message:
        "Você ainda não enviou os SPEDs Fiscais no sistema. Carregue os arquivos na tela de Documentos para visualizar produtos, impostos e movimentações.",
    },
    clients: {
      title: "Nenhum cliente disponível",
      message:
        "Você ainda não enviou os SPEDs Fiscais no sistema. Carregue os arquivos na tela de Documentos para visualizar clientes, faturamento e impostos.",
    },
    suppliers: {
      title: "Nenhum fornecedor disponível",
      message:
        "Você ainda não enviou os SPEDs Fiscais no sistema. Carregue os arquivos na tela de Documentos para visualizar fornecedores, compras e impostos.",
    },
    transactions: {
      title: "Nenhuma movimentação disponível",
      message:
        "Você ainda não enviou os SPEDs Fiscais no sistema. Carregue os arquivos na tela de Documentos para visualizar movimentações, NFe e CTe.",
    },
    certificates: {
      title: "Nenhum certificado encontrado",
      message:
        "Para capturar notas fiscal eletrônicas automaticamente, é necessário carregar o certificado digital (.pfx) do CNPJ na tela de Certificados.",
    },
    "reforma-tributaria": {
      title: "Sem dados suficientes para o Simulador",
      message:
        "Para calcular o impacto da Reforma Tributária com dados reais, é necessário carregar e processar os SPEDs Fiscal (ICMS/IPI) e Contribuições (PIS/COFINS) deste projeto na tela de Documentos.",
    },
  };

  const { title, message } = messages[screen] || messages.products;

  const resolvedTitle = customTitle || title;
  const resolvedMessage = customMessage || message;
  const resolvedCtaText = customCtaText || (screen === 'certificates' ? 'Carregar Certificado' : 'Carregar SPEDs');
  const resolvedOnCta = customOnCta || (screen === 'certificates' ? goToCertificates : goToFiles);
  const resolvedIcon = customIcon || <GoAlert />;
  const resolvedHint = customHint || (
    screen === 'certificates'
      ? <>Dica: Acesse <a href="#" onClick={(e) => { e.preventDefault(); goToCertificates(); }}>Arquivos → Certificados</a> para fazer upload</>
      : <>Dica: Acesse <a href="#" onClick={(e) => { e.preventDefault(); goToFiles(); }}>Arquivos → SPEDs</a> para fazer upload</>
  );

  return (
    <Wrapper $compact={compact}>
      <div className="icon">
        {resolvedIcon}
      </div>
      <h2 className="title">{resolvedTitle}</h2>
      <p className="message">{resolvedMessage}</p>
      {resolvedOnCta && (
        <button className="cta" onClick={resolvedOnCta}>
          <GoUpload />
          {resolvedCtaText}
        </button>
      )}
      <p className="hint">
        {resolvedHint}
      </p>
    </Wrapper>
  );
}
